import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchCourses, createOrEditCourse } from "../../utils/wellspoken.api";


export default function EditCourse() {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  
  const [form, setForm] = useState({
    courseId: "",
    courseName: "",
    courseDescription: "",
    courseDuration: "",
    price: "",
    courseLevel: "",
    courseMode: "",
    startDate: "",
    endDate: "",
    items: [""],
  });

  /* ---------------- Load Course ---------------- */
  useEffect(() => {
    fetchCourses()
      .then((data) => {
        const course = data.find(
          (c) => String(c.courseId) === String(courseId)
        );

        if (!course) {
          setError("Course not found.");
          return;
        }

        setForm({
          courseId: course.courseId,
          courseName: course.courseName || "",
          courseDescription: course.courseDescription || "",
          courseDuration: course.courseDuration || "",
          price: course.price || "",
          courseLevel: course.courseLevel || "",
          courseMode: course.courseMode || "",
          startDate: course.startDate || "",
          endDate: course.endDate || "",
          items: Array.isArray(course.items) && course.items.length
            ? course.items
            : [""],
        });

        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load course data.");
        setLoading(false);
      });
  }, [courseId]);

  /* ---------------- Handlers ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, value) => {
    const updated = [...form.items];
    updated[index] = value;
    setForm((prev) => ({ ...prev, items: updated }));
  };

  const addItem = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, ""] }));
  };

  const removeItem = (index) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      await createOrEditCourse({
        CourseId: form.courseId,
        CourseName: form.courseName,
        CourseDescription: form.courseDescription,
        CourseDuration: form.courseDuration,
        Price: form.price,
        CourseLevel: form.courseLevel,
        CourseMode: form.courseMode,
        StartDate: form.startDate,
        EndDate: form.endDate,
        Items: form.items.filter(Boolean),
      });

      navigate("/dashboard/courses");
    } catch {
      setError("Failed to update course.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <main className="bg-surface min-h-screen py-16">
      <section className="max-w-4xl mx-auto px-4">
        <header className="mb-10 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-textPrimary">
            Edit Course
          </h1>
          <p className="mt-2 text-textSecondary">
            Update course details and save changes
          </p>
        </header>

        {loading && (
          <p className="text-center text-textSecondary">Loading course…</p>
        )}

        {error && (
          <p className="text-center text-primary-light mb-6">{error}</p>
        )}

        {!loading && (
          <form
            onSubmit={handleSubmit}
            className="
              bg-white border border-border rounded-2xl
              p-6 sm:p-8 space-y-6
              focus-within:ring-2 focus-within:ring-primary-light
            "
          >
            {/* Course Name */}
            <div>
              <label className="block text-sm font-medium text-textPrimary">
                Course Name
              </label>
              <input
                name="courseName"
                value={form.courseName}
                onChange={handleChange}
                required
                className="mt-1 w-full rounded-lg border border-border px-4 py-2 focus:ring-primary-light"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-textPrimary">
                Description
              </label>
              <textarea
                name="courseDescription"
                value={form.courseDescription}
                onChange={handleChange}
                rows={3}
                className="mt-1 w-full rounded-lg border border-border px-4 py-2 focus:ring-primary-light"
              />
            </div>

            {/* Grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              <inputField label="Duration (months)" name="courseDuration" value={form.courseDuration} onChange={handleChange} />
              <inputField label="Price (₹)" name="price" value={form.price} onChange={handleChange} />
              <inputField label="Level" name="courseLevel" value={form.courseLevel} onChange={handleChange} />
              <inputField label="Mode" name="courseMode" value={form.courseMode} onChange={handleChange} />
              <inputField label="Start Date" name="startDate" type="date" value={form.startDate} onChange={handleChange} />
              <inputField label="End Date" name="endDate" type="date" value={form.endDate} onChange={handleChange} />
            </div>

            {/* Items */}
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Course Highlights
              </label>

              {form.items.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    value={item}
                    onChange={(e) =>
                      handleItemChange(index, e.target.value)
                    }
                    className="flex-1 rounded-lg border border-border px-3 py-2"
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    aria-label="Remove item"
                    className="text-primary-light font-semibold"
                  >
                    ✕
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={addItem}
                className="text-sm text-primary font-medium mt-2"
              >
                + Add item
              </button>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-5 py-2 rounded-full border border-border"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="
                  px-6 py-2 rounded-full
                  bg-primary text-white font-semibold
                  hover:bg-primary-dark
                  focus-visible:ring-2 focus-visible:ring-primary-light
                "
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </form>
        )}
      </section>
    </main>
  );
}

/* --------- Reusable Field ---------- */
// function inputField({ label, ...props }) {
//   return (
//     <div>
//       <label className="block text-sm font-medium text-textPrimary">
//         {label}
//       </label>
//       <input
//         {...props}
//         className="mt-1 w-full rounded-lg border border-border px-4 py-2 focus:ring-primary-light"
//       />
//     </div>
//   );
// }
